import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Rocket, Loader2, Delete, Mail, ArrowLeft, Users2, KeyRound } from "lucide-react";

function formatErr(detail) {
  if (!detail) return "Access denied. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { loginPin, login, staffLogin, changeCode } = useAuth();
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("pin"); // 'pin' | 'email' | 'staff' | 'changecode'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [code, setCode] = useState("");
  const [newCode, setNewCode] = useState("");
  const [newCode2, setNewCode2] = useState("");
  const [roster, setRoster] = useState([]);
  useEffect(() => {
    if (mode === "staff" && roster.length === 0) {
      api.get("/crm/staff/roster").then((r) => setRoster(r.data.login || [])).catch(() => {});
    }
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitPin = async (value) => {
    setError(""); setLoading(true);
    try {
      await loginPin(value);
      navigate("/admin");
    } catch (err) {
      setError(formatErr(err.response?.data?.detail));
      setPin("");
    } finally { setLoading(false); }
  };

  const press = (d) => {
    if (loading) return;
    const next = (pin + d).slice(0, 8);
    setPin(next);
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await login(email, password); navigate("/admin"); }
    catch (err) { setError(formatErr(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const submitStaff = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await staffLogin(username.trim().toLowerCase(), code);
      if (u.must_change) { setMode("changecode"); }
      else navigate("/admin");
    } catch (err) { setError(formatErr(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const submitChangeCode = async (e) => {
    e.preventDefault();
    setError("");
    if (newCode !== newCode2) { setError("Codes do not match."); return; }
    if (!/^\d{4,8}$/.test(newCode)) { setError("Code must be 4–8 digits."); return; }
    if (newCode === "1111") { setError("Please choose a code different from the default."); return; }
    setLoading(true);
    try { await changeCode(newCode); navigate("/admin"); }
    catch (err) { setError(formatErr(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#080b11] dot-grid px-4" data-testid="admin-login">
      <div className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-launch via-cool to-transparent" />
      <div className="w-full max-w-sm">
        <div className="text-center">
          <img src="/logo-header.webp" alt="Aquila Air & Heating" className="h-14 w-auto mx-auto" width="1206" height="356" />
          <h1 className="mt-6 font-heading text-2xl font-extrabold text-white tracking-tight">Aquila Command Center</h1>
          <p className="mt-1 text-sm text-slate-400">Sign in to manage your business</p>
        </div>

        <div className="mt-8 bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-xl">
          {mode === "pin" && (
            <>
              <Label className="text-slate-300 text-xs uppercase tracking-widest">Access PIN</Label>
              <div className="mt-3 flex justify-center gap-3" data-testid="pin-display">
                {Array.from({ length: Math.max(4, pin.length) }).map((_, i) => (
                  <span key={i} className={`h-3.5 w-3.5 rounded-full transition-colors ${i < pin.length ? "bg-launch" : "bg-white/15"}`} />
                ))}
              </div>
              <div className="mt-6 grid grid-cols-3 gap-3">
                {["1","2","3","4","5","6","7","8","9"].map((d) => (
                  <button key={d} type="button" onClick={() => press(d)} data-testid={`pin-${d}`}
                    className="h-14 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xl font-semibold transition-colors">{d}</button>
                ))}
                <button type="button" onClick={() => setPin("")} className="h-14 rounded-xl text-slate-400 hover:text-white text-sm">Clear</button>
                <button type="button" onClick={() => press("0")} data-testid="pin-0" className="h-14 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xl font-semibold transition-colors">0</button>
                <button type="button" onClick={() => setPin(pin.slice(0, -1))} className="h-14 rounded-xl text-slate-400 hover:text-white flex items-center justify-center"><Delete className="h-5 w-5" /></button>
              </div>
              {error && <p className="mt-4 text-sm text-red-400 text-center" data-testid="admin-error">{error}</p>}
              <button type="button" onClick={() => submitPin(pin)} disabled={loading || pin.length < 4} data-testid="pin-submit"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold py-3 rounded-xl transition-colors disabled:opacity-50">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} Enter Dashboard
              </button>
              <div className="mt-4 flex items-center justify-between">
                <button type="button" onClick={() => { setMode("staff"); setError(""); }} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1" data-testid="staff-login-link">
                  <Users2 className="h-3.5 w-3.5" /> Team member sign in
                </button>
                <button type="button" onClick={() => { setMode("email"); setError(""); }} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1">
                  <Mail className="h-3.5 w-3.5" /> Email
                </button>
              </div>
            </>
          )}

          {mode === "email" && (
            <form onSubmit={submitEmail} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-cool" data-testid="admin-email" required />
              </div>
              <div>
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white focus-visible:ring-cool" data-testid="admin-password" required />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="admin-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="admin-login-submit" className="w-full inline-flex items-center justify-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold py-3 rounded-xl transition-colors disabled:opacity-60">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign In
              </button>
              <button type="button" onClick={() => { setMode("pin"); setError(""); }} className="w-full text-xs text-slate-400 hover:text-white inline-flex items-center justify-center gap-1">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to PIN entry
              </button>
            </form>
          )}

          {mode === "staff" && (
            <form onSubmit={submitStaff} className="space-y-4" data-testid="staff-login-form">
              <div>
                <Label htmlFor="username" className="text-slate-300">Select your name</Label>
                <select id="username" value={username} onChange={(e) => setUsername(e.target.value)} data-testid="staff-username-select" required
                  className="mt-1 w-full rounded-md bg-white/5 border border-white/15 text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cool">
                  <option value="" className="text-slate-900">— Choose your name —</option>
                  {roster.map((m) => <option key={m.username} value={m.username} className="text-slate-900">{m.name}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="code" className="text-slate-300">Login code</Label>
                <Input id="code" type="password" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="First-time code is 1111" className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-cool" data-testid="staff-code" required />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="admin-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="staff-login-submit" className="w-full inline-flex items-center justify-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold py-3 rounded-xl transition-colors disabled:opacity-60">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign In
              </button>
              <button type="button" onClick={() => { setMode("pin"); setError(""); }} className="w-full text-xs text-slate-400 hover:text-white inline-flex items-center justify-center gap-1">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
            </form>
          )}

          {mode === "changecode" && (
            <form onSubmit={submitChangeCode} className="space-y-4" data-testid="changecode-form">
              <div className="flex items-center gap-2 text-launch"><KeyRound className="h-5 w-5" /><p className="font-heading font-bold">Set your new login code</p></div>
              <p className="text-xs text-slate-400">For security, choose a personal 4–8 digit code (not 1111).</p>
              <div>
                <Label htmlFor="newcode" className="text-slate-300">New code</Label>
                <Input id="newcode" type="password" inputMode="numeric" value={newCode} onChange={(e) => setNewCode(e.target.value.replace(/\D/g, ""))} className="mt-1 bg-white/5 border-white/15 text-white focus-visible:ring-cool" data-testid="new-code" required />
              </div>
              <div>
                <Label htmlFor="newcode2" className="text-slate-300">Confirm new code</Label>
                <Input id="newcode2" type="password" inputMode="numeric" value={newCode2} onChange={(e) => setNewCode2(e.target.value.replace(/\D/g, ""))} className="mt-1 bg-white/5 border-white/15 text-white focus-visible:ring-cool" data-testid="new-code2" required />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="admin-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="changecode-submit" className="w-full inline-flex items-center justify-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold py-3 rounded-xl transition-colors disabled:opacity-60">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} Save &amp; Continue
              </button>
            </form>
          )}
        </div>
        <p className="mt-6 text-center text-xs text-slate-500 tracking-wide">Authorized personnel only</p>
      </div>
    </div>
  );
}
